import { Tile } from './Tiles';
import { isSimple, isHonor, isTerminal, isTerminalOrHonor, getCounts, decomposeHand } from './HandAnalysis';

export interface YakuContext {
    isRiichi?: boolean;
    isClosed?: boolean;
    // Add more context as needed (e.g., round wind, seat wind, winning tile)
}

export interface Yaku {
    name: string;
    han_closed: number;
    han_open: number | null; // null means closed only
    description: string;
    example: Tile[];
    check: (hand: Tile[], context?: YakuContext) => boolean;
}

const t = (type: any, value: any): Tile => ({ id: `${type}-${value}-${Math.random()}`, type, value, isRed: false } as Tile);
const m = (v: number) => t('man', v);
const p = (v: number) => t('pin', v);
const s = (v: number) => t('sou', v);
const w = (v: string) => t('wind', v);
const d = (v: string) => t('dragon', v);

// --- Yaku Definitions ---

export const YAKU_DATA: Yaku[] = [
    {
        name: "Riichi", han_closed: 1, han_open: null, description: "Declaring ready hand. Must be closed.",
        example: [m(1), m(2), m(3), p(4), p(5), p(6), s(7), s(8), s(9), m(5), m(5), p(1), p(1), p(1)],
        check: (hand, ctx) => !!ctx?.isRiichi && !!ctx?.isClosed
    },
    {
        name: "Tanyao (All Simples)", han_closed: 1, han_open: 1, description: "Hand containing only simple tiles (2-8). No terminals or honors.",
        example: [m(2), m(3), m(4), p(6), p(7), p(8), s(3), s(3), s(3), s(6), s(7), s(8), m(5), m(5)],
        check: (hand) => hand.every(isSimple)
    },
    {
        name: "Pinfu", han_closed: 1, han_open: null, description: "Flat hand. No triplets, no kan, valueless pair, ryanmen wait. Must be closed.",
        example: [m(1), m(2), m(3), p(4), p(5), p(6), s(7), s(8), s(9), m(4), m(5), m(6), s(1), s(1)],
        check: (hand, ctx) => {
            if (!ctx?.isClosed) return false;
            const decomps = decomposeHand(hand);
            // Simplified check: must have valid decomposition where all sets are sequences and pair is not value
            // Note: Proper Pinfu check requires checking the wait (ryanmen), which we can't fully do without knowing the winning tile state.
            // We will approximate by checking for all sequences + non-value pair.
            return decomps.some(d => {
                const allSequences = d.sets.every(s => s[0].value !== s[1].value); // Sequence if values differ
                const pair = d.pair[0];
                const isValuePair = pair.type === 'dragon' || pair.type === 'wind'; // Simplified value pair check
                return allSequences && !isValuePair;
            });
        }
    },
    {
        name: "Iipeiko (Pure Double Sequence)", han_closed: 1, han_open: null, description: "Two identical sequences of the same suit. Must be closed.",
        example: [m(2), m(2), m(3), m(3), m(4), m(4), p(5), p(6), p(7), s(8), s(8), s(8), w('north'), w('north')],
        check: (hand, ctx) => {
            if (!ctx?.isClosed) return false;
            const decomps = decomposeHand(hand);
            return decomps.some(d => {
                const seqs = d.sets.filter(s => s[0].value !== s[1].value).map(s => `${s[0].type}_${s[0].value}`);
                const uniqueSeqs = new Set(seqs);
                return seqs.length - uniqueSeqs.size >= 1; // At least one duplicate sequence
            });
        }
    },
    {
        name: "Yakuhai (Value Tiles)", han_closed: 1, han_open: 1, description: "Triplet or quad of Dragons, Round Wind, or Seat Wind.",
        example: [d('white'), d('white'), d('white'), m(1), m(2), m(3), p(7), p(8), p(9), s(5), s(5), s(5), m(9), m(9)],
        check: (hand) => {
            const counts = getCounts(hand);
            // Check for dragon triplets
            if (counts['dragon_white'] >= 3 || counts['dragon_green'] >= 3 || counts['dragon_red'] >= 3) return true;
            // Note: Should check winds based on context, skipping for now
            return false;
        }
    },
    {
        name: "Sanshoku Doujun (Mixed Triple Sequence)", han_closed: 2, han_open: 1, description: "Three sequences of the same numbers in all three suits.",
        example: [m(1), m(2), m(3), p(1), p(2), p(3), s(1), s(2), s(3), m(5), m(5), p(8), p(8), p(8)],
        check: (hand) => {
            const decomps = decomposeHand(hand);
            return decomps.some(d => {
                const seqs = d.sets.filter(s => s[0].value !== s[1].value);
                const man = seqs.filter(s => s[0].type === 'man').map(s => s[0].value);
                const pin = seqs.filter(s => s[0].type === 'pin').map(s => s[0].value);
                const sou = seqs.filter(s => s[0].type === 'sou').map(s => s[0].value);
                return man.some(v => pin.includes(v) && sou.includes(v));
            });
        }
    },
    {
        name: "Itsu (Pure Straight)", han_closed: 2, han_open: 1, description: "Straight from 1 to 9 in the same suit.",
        example: [m(1), m(2), m(3), m(4), m(5), m(6), m(7), m(8), m(9), p(1), p(1), s(5), s(5), s(5)],
        check: (hand) => {
            const decomps = decomposeHand(hand);
            return decomps.some(d => {
                const seqs = d.sets.filter(s => s[0].value !== s[1].value);
                const suits = ['man', 'pin', 'sou'];
                return suits.some(suit => {
                    const suitSeqs = seqs.filter(s => s[0].type === suit).map(s => s[0].value);
                    return suitSeqs.includes(1) && suitSeqs.includes(4) && suitSeqs.includes(7);
                });
            });
        }
    },
    {
        name: "Chanta (Mixed Outside Hand)", han_closed: 2, han_open: 1, description: "Every set and the pair contains at least one terminal or honor.",
        example: [m(1), m(2), m(3), p(7), p(8), p(9), s(1), s(2), s(3), w('east'), w('east'), w('east'), d('red'), d('red')],
        check: (hand) => {
            const decomps = decomposeHand(hand);
            return decomps.some(d => {
                const allSetsValid = d.sets.every(s => s.some(isTerminalOrHonor));
                const pairValid = isTerminalOrHonor(d.pair[0]);
                // Must contain at least one honor (otherwise it's Junchan)
                const hasHonor = hand.some(isHonor);
                return allSetsValid && pairValid && hasHonor;
            });
        }
    },
    {
        name: "Chiitoitsu (Seven Pairs)", han_closed: 2, han_open: null, description: "Seven pairs of different tiles. Must be closed.",
        example: [m(1), m(1), m(5), m(5), p(2), p(2), p(9), p(9), s(3), s(3), s(7), s(7), w('east'), w('east')],
        check: (hand, ctx) => {
            if (ctx?.isClosed === false) return false;
            const counts = getCounts(hand);
            return Object.values(counts).every(c => c === 2) && Object.keys(counts).length === 7;
        }
    },
    {
        name: "Toitoi (All Triplets)", han_closed: 2, han_open: 2, description: "Hand consisting of four triplets/quads and a pair.",
        example: [m(1), m(1), m(1), p(2), p(2), p(2), s(3), s(3), s(3), m(5), m(5), m(5), d('white'), d('white')],
        check: (hand) => {
            const decomps = decomposeHand(hand);
            return decomps.some(d => d.sets.every(s => s[0].value === s[1].value));
        }
    },
    {
        name: "Sanankou (Three Concealed Triplets)", han_closed: 2, han_open: 2, description: "Three triplets formed without calling.",
        example: [m(1), m(1), m(1), p(2), p(2), p(2), s(3), s(3), s(3), m(4), m(5), m(6), d('red'), d('red')],
        check: (hand, ctx) => {
            // Approximation: check if there are 3 triplets in hand. 
            // Correct check requires knowing which parts are open/closed calls.
            // Assuming hand passed here is the full hand content.
            if (ctx?.isClosed === false) return false; // Simplified: assume closed for now or need call info
            const decomps = decomposeHand(hand);
            return decomps.some(d => d.sets.filter(s => s[0].value === s[1].value).length >= 3);
        }
    },
    {
        name: "Honitsu (Half Flush)", han_closed: 3, han_open: 2, description: "Hand containing tiles from only one suit and honors.",
        example: [m(1), m(2), m(3), m(5), m(5), m(7), m(8), m(9), w('east'), w('east'), w('east'), d('white'), d('white'), d('white')],
        check: (hand) => {
            const suits = new Set(hand.filter(t => !isHonor(t)).map(t => t.type));
            const hasHonors = hand.some(isHonor);
            return suits.size === 1 && hasHonors;
        }
    },
    {
        name: "Junchan (Pure Outside Hand)", han_closed: 3, han_open: 2, description: "Every set and the pair contains at least one terminal.",
        example: [m(1), m(2), m(3), m(7), m(8), m(9), p(1), p(2), p(3), s(7), s(8), s(9), m(1), m(1)],
        check: (hand) => {
            const decomps = decomposeHand(hand);
            return decomps.some(d => {
                const allSetsValid = d.sets.every(s => s.some(isTerminal));
                const pairValid = isTerminal(d.pair[0]);
                return allSetsValid && pairValid;
            });
        }
    },
    {
        name: "Ryanpeiko (Twice Pure Double Sequence)", han_closed: 3, han_open: null, description: "Two sets of Iipeiko. Must be closed.",
        example: [m(2), m(2), m(3), m(3), m(4), m(4), p(5), p(5), p(6), p(6), p(7), p(7), s(9), s(9)],
        check: (hand, ctx) => {
            if (!ctx?.isClosed) return false;
            const decomps = decomposeHand(hand);
            return decomps.some(d => {
                const seqs = d.sets.filter(s => s[0].value !== s[1].value).map(s => `${s[0].type}_${s[0].value}`);
                const uniqueSeqs = new Set(seqs);
                return seqs.length === 4 && uniqueSeqs.size <= 2; // 2 pairs of identical sequences
            });
        }
    },
    {
        name: "Chinitsu (Full Flush)", han_closed: 6, han_open: 5, description: "Hand containing tiles from only one suit.",
        example: [m(1), m(1), m(1), m(2), m(3), m(4), m(5), m(6), m(7), m(8), m(9), m(9), m(9), m(5)],
        check: (hand) => {
            const suits = new Set(hand.map(t => t.type));
            return suits.size === 1 && !isHonor(hand[0]);
        }
    },
    {
        name: "Kokushi Musou (Thirteen Orphans)", han_closed: 13, han_open: null, description: "One of each terminal and honor, plus one duplicate. Yakuman.",
        example: [m(1), m(9), p(1), p(9), s(1), s(9), w('east'), w('south'), w('west'), w('north'), d('white'), d('green'), d('red'), m(1)],
        check: (hand) => {
            const counts = getCounts(hand);
            const keys = Object.keys(counts);
            if (keys.length !== 13) return false;
            const required = [
                'man_1', 'man_9', 'pin_1', 'pin_9', 'sou_1', 'sou_9',
                'wind_east', 'wind_south', 'wind_west', 'wind_north',
                'dragon_white', 'dragon_green', 'dragon_red'
            ];
            return required.every(k => counts[k] >= 1);
        }
    },
];

export function checkHand(hand: Tile[], context?: YakuContext): Yaku[] {
    // Basic validation: Hand must have 14 tiles (or 13 + win)
    if (hand.length !== 14) return [];

    return YAKU_DATA.filter(yaku => {
        try {
            return yaku.check(hand, context);
        } catch (e) {
            console.error(`Error checking yaku ${yaku.name}`, e);
            return false;
        }
    });
}